<template>
  <div class="templates-wrapper">
    <div class="templates-header">
      <h4>Choose a template for your campaign:</h4>
      <Button label="Build My Own" class="p-button-link" @click="createCustomTemplate" :class="{selected: isSelectedCustomTemplate}" />
    </div>

    <p>Select the template you would like to order. You can customize it in the next step.</p>

    <div class="templates">

      <div class="template" v-for="template in templates" :key="template.id" :class="{selected: isSelected(template)}">
        <div class="video-wrapper">
          <VideoPlayer v-if="template.video" :src="template.video.url" :isShowPoster="false" :autoPlay="false" type="fluid" />
        </div>
        <div class="template-content">
          <div class="title">{{ template.title }}</div>
          <div class="text">{{ template.content }}</div>
          <div class="buttons p-mt-auto">
            <Button v-if="isSelected(template)" class="p-button-secondary p-button-md full-width p-justify-center" @click="removeSelection(template)">
              <i class="pi pi-times p-mr-2"></i>
              Remove selection
            </Button>
            <Button v-else label="Select template" class="full-width p-button-md" @click="select(template)" />
          </div>
        </div>
      </div>

    </div>
  </div>

  <Footer>
    <Button label="Previous step" class="p-button-text p-button-secondary" icon="pi pi-angle-left" @click.prevent="emit('onBack')" />
    <Button label="Next Step" class="p-ml-auto next-button p-button-md p-button-footer" @click.prevent="onNextStep" :disabled="!canMoveToNextStep" />
  </Footer>
</template>

<script setup>
import Footer from "./Footer";
import {useAdTemplate} from "../../../hooks/useAdTemplate";
import {computed} from "vue";
import VideoPlayer from "../../VideoPlayer";

const emit = defineEmits(['onBack', 'onNext', 'update:campaign']);
const props = defineProps({
  campaign: {type: Object, required: true}
});

const {getTemplates} = useAdTemplate();
const templates = (await getTemplates()).data;

const canMoveToNextStep = computed(() => props.campaign.ad_template_id || props.campaign.is_custom_template);
const isSelectedCustomTemplate = computed(() => props.campaign.is_custom_template);

const select = template => {
  emit('update:campaign', {
    ...props.campaign,
    is_custom_template: false,
    ad_template_id: template.id,
  });

  emit('onNext');
}

const removeSelection = template => {
  emit('update:campaign', {
    ...props.campaign,
    ad_template_id: null,
  });
}

const isSelected = template => props.campaign.ad_template_id === template.id;
const onNextStep = () => emit('onNext');

const createCustomTemplate = () => {
  emit('update:campaign', {
    ...props.campaign,
    is_custom_template: true,
    ad_template_id: null
  });
}

</script>

<style lang="scss" scoped>
@import "../../../assets/sass/theme_variables";

.templates-wrapper {
  max-width: toRem(924);
  margin: 0 auto;
}

.templates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: toRem(16);
  margin-top: toRem(30);
  margin-bottom: toRem(30);
}

.template {
  display: flex;
  border: 1px solid #DFE0E6;
  border-radius: 10px;

  &.selected {
    border-color: $primaryColor;
    filter: drop-shadow(0px 0px 10px rgba(68, 144, 234, 0.3));
  }
}

.video-wrapper {
  position: relative;
  width: toRem(184);
  height: toRem(330);
  flex: 0 0 auto;
  border-radius: 10px;
}

.play-button {
  display: flex;
  position: absolute;
  left: 50%;
  top: 50%;
  width: 66px;
  height: 66px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: 2px solid $bgBlocks;
  box-sizing: border-box;
  backdrop-filter: blur(12px);
  transform: translate(-50%, -50%);
  cursor: pointer;
  justify-content: center;
  align-items: center;

  &:before {
    width: toRem(17);
    height: toRem(19);
    background: url("../../../assets/images/play.svg");
    content: '';
  }
}

.title {
  font-weight: bold;
  font-size: toRem(20);
}

.text {
  margin-top: 5px;
  color: $textSecondaryColor;
}

.template-content {
  display: flex;
  flex-direction: column;
  padding: toRem(16);
}

.buttons {
  padding-top: 1rem;

  .p-button {
    font-weight: 500;
  }

  .pi {
    font-size: toRem(12);
  }
}

.templates-header {
  display: flex;
  justify-content: space-between;

  .p-button {
    font-weight: 600;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }

    &.selected {
      border: $buttonBorder;
    }
  }
}
</style>
